import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    DisplayMapMarker,
    InfiniteScroll,
    inject,
    Map as MapComponent,
    NothingHere,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsCardHeading } from '@react-icons/all-files/bs/BsCardHeading';
import { AccountsStore } from '../Accounts/AccountsStore';
import { AppStore } from '../Core/AppStore';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { ActiveDisplay } from './ActiveDisplay';
import { MapDisplayItem } from './MapDisplayItem';
import {AccountStore} from "../Account/AccountStore";
import {debounce} from "lodash";
import {DropdownItem} from "reactstrap";

export interface IDisplayListMapProps {
    readonly displays: ActiveDisplay[];
    readonly vm: DisplayListViewModel;
    readonly history: any;
}

@autoinject
@observer
export class DisplayListMap extends React.Component<IDisplayListMapProps> {
    @action
    public async componentDidMount() {
        this.accountsStore.loadAsync();
        
        if(this.accountStore.storage.mapShowDisplayList == undefined){
            this.accountStore.storage.mapShowDisplayList = true;
        }
        this.showDisplayList = this.accountStore.storage.mapShowDisplayList;
    }

    @computed
    private get latLngs(): google.maps.LatLngLiteral[] {
        if (this.props.displays) {
            return this.props.displays.map(d => {
                const gpsLat = d.measurements?.find(s => s.id == Mri.WebApi.MeasurementId.GpsLatitude)?.floatValue;
                const smartVuLat = d.unitLocation?.latitude;
                const latitude = gpsLat ? gpsLat : smartVuLat;

                const gpsLong = d?.measurements?.find(s => s.id == Mri.WebApi.MeasurementId.GpsLongitude)?.floatValue;
                const smartVuLong = d.unitLocation?.longitude;
                const longitude = gpsLong ? gpsLong : smartVuLong;

                return { lat: latitude || 40.756795, lng: longitude || -73.954298 };
            });
        } else {
            return [];
        }
    }
    public render() {
        const { hasMore, loadMoreAsync, displays, busy } = this.props.vm;
        return (
            <div className={`display-map-list-container ${this.showDisplayList ? '' : 'list-hidden'}`}>
                <div className="map-section">
                    {this.renderMap && (
                        <MapComponent
                            dark={this.appStore.storage.theme == 'dark-theme'}
                            height="100%"
                            mapRef={this.setMapRef}
                            zoom={this.accountStore.storage.mapZoomLevel}
                            onZoom={this.onZoomChange}
                            latLngs={this.latLngs}
                            mapOptionsChildren={
                                <button className='dropdown-item' onClick={this.toggleDisplayList}>

                                    {this.showDisplayList ? 'Hide ' : 'Show '} List
                                </button>
                            }
                        >
                            {this.props.displays.map(d => (
                                <DisplayMapMarker
                                    key={d.serialNumber}
                                    draggable={false}
                                    serialNumber={d.serialNumber}
                                    lat={this.getLatitude(d) ?? 40.756795}
                                    lng={this.getLongitude(d) ?? -73.954298}
                                    address1={d.fullAddress}
                                    color={this.markerColor(d)}
                                    notification={this.notification(d)}
                                    ref={r => this.mapMarkerRef(d.serialNumber, r)}
                                />
                            ))}
                        </MapComponent>
                    )}

                    {!this.renderMap && (
                        <div className="text-center">
                            - Map can only load up to 500 displays. Add more filters to decrease the number of
                            displays loaded -
                        </div>
                    )}
                </div>
                {this.showDisplayList && (
                    <div className="list-section">
                        <div ref={this.setListContainerRef} className="map-display-list container-fluid pt-2">
                            {
                                <InfiniteScroll
                                    throttle={100}
                                    threshold={300}
                                    hasMore={hasMore}
                                    container={this.listCountainer}
                                    onLoadMore={loadMoreAsync}
                                    className="row"
                                >
                                    {displays?.map((d, idx) => (
                                        <MapDisplayItem
                                            onClick={() => this.panToMarker(d)}
                                            account={this.accountsStore.accounts.get(d.account)}
                                            display={d}
                                            key={idx}
                                            history={this.props.history}
                                        />
                                    ))}

                                    <NothingHere
                                        empty={!displays.length}
                                        icon={<BsCardHeading/>}
                                        loading={busy}
                                        label="No Displays Returned"
                                        loadingMessage="Loading Displays..."
                                        className="my-5"
                                        forceLoaderOnLoad
                                    />
                                </InfiniteScroll>
                            }
                        </div>
                    </div>
                )}
            </div>
        );
    }

    public markerColor(display: ActiveDisplay) {
        if (
            display.displayStatus == Mri.DisplayStatuses.DisplayStatus.Online &&
            (display.displayHealth == Mri.DisplayStatuses.DisplayHealth.Warning ||
                display.displayHealth == Mri.DisplayStatuses.DisplayHealth.Critical) &&
            display.backlightStatus != Mri.DisplayStatuses.BacklightState.Disabled &&
            (display.videoStatus == Mri.DisplayStatuses.VideoState.Valid ||
                display.backlightStatus == Mri.DisplayStatuses.BacklightState.StandbyMode)
        ) {
            return 'warning';
        } else if (
            display.backlightStatus != Mri.DisplayStatuses.BacklightState.Disabled &&
            display.videoStatus != Mri.DisplayStatuses.VideoState.NotConnected &&
            (display.videoStatus == Mri.DisplayStatuses.VideoState.Valid ||
                display.backlightStatus == Mri.DisplayStatuses.BacklightState.StandbyMode)
        ) {
            return 'good';
        } else if (
            display.backlightStatus == Mri.DisplayStatuses.BacklightState.Disabled ||
            display.videoStatus == Mri.DisplayStatuses.VideoState.InternalError ||
            display.videoStatus == Mri.DisplayStatuses.VideoState.InvalidResolution ||
            display.videoStatus == Mri.DisplayStatuses.VideoState.NotConnected
        ) {
            return 'danger';
        } else {
            return 'inactive';
        }
    }

    public notification(display: ActiveDisplay) {
        return (
            display.displayStatus == Mri.DisplayStatuses.DisplayStatus.OfflineOrInactive ||
            display.displayStatus == Mri.DisplayStatuses.DisplayStatus.NeverCommunicated ||
            display.displayStatus == Mri.DisplayStatuses.DisplayStatus.NocCouldNotConnect ||
            display.displayStatus == Mri.DisplayStatuses.DisplayStatus.EngineOffTimedOut ||
            display.displayStatus == Mri.DisplayStatuses.DisplayStatus.TimedOut
        );
    }

    @bind
    @action
    private setZoom(zoom: number) {
        this.accountStore.storage.mapZoomLevel = zoom;
    }

    @action
    private onZoomChange = debounce((zoom: number) => {
        this.setZoom(zoom);
    }, 1000);

    @bind
    @action
    private setMapRef(r: google.maps.Map | null) {
        this.map = r;
    }

    @bind
    @action
    private setListContainerRef(r: HTMLDivElement | null) {
        this.listCountainer = r;
    }

    @bind
    private panToMarker(display: ActiveDisplay) {
        const { serialNumber } = display;
        const lat = this.getLatitude(display);
        const lng = this.getLongitude(display);
        if (this.map && lat && lng) {
            this.map.panTo({
                lat,
                lng,
            });

            const displayMapMarker = this.displayMapMarkers.get(serialNumber);

            if (displayMapMarker) displayMapMarker.toggleWindow();
        }
    }

    @bind
    @action
    private mapMarkerRef(serialNumber: string, displayMapMarker: DisplayMapMarker | null) {
        if (displayMapMarker) this.displayMapMarkers.set(serialNumber, displayMapMarker);
        else this.displayMapMarkers.delete(serialNumber);
    }

    @bind
    @action
    private getLatitude(display: ActiveDisplay) {
        const gpsLat = display?.measurements?.find(s => s.id == Mri.WebApi.MeasurementId.GpsLatitude)?.floatValue;
        const smartVuLat = display.unitLocation?.latitude;
        return gpsLat ? gpsLat : smartVuLat;
    }

    @bind
    @action
    private getLongitude(display: ActiveDisplay) {
        const gpsLong = display?.measurements?.find(s => s.id == Mri.WebApi.MeasurementId.GpsLongitude)?.floatValue;
        const smartVuLong = display.unitLocation?.longitude;

        return gpsLong ? gpsLong : smartVuLong;
    }

    @bind
    @action
    private toggleDisplayList(){
        this.showDisplayList = !this.showDisplayList;
        this.accountStore.storage.mapShowDisplayList = this.showDisplayList;
    }

    @computed
    private get renderMap() {
        if (this.latLngs.length > 500) {
            return false;
        }
        return true;
    }

    @observable
    private showDisplayList: boolean = true;
    @inject
    private appStore!: AppStore;
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private accountStore!: AccountStore;
    @observable
    private map: google.maps.Map | null = null;
    private listCountainer: HTMLDivElement | null = null;
    private displayMapMarkers: Map<string, DisplayMapMarker> = new Map<string, DisplayMapMarker>();
}
